import { useQuery } from '@tanstack/react-query'
import {
  generatePath,
  Navigate,
  NavLink,
  Outlet,
  useLocation,
  useParams,
  useSearchParams,
} from 'react-router-dom'

import { fetchMultisigGroupByAccount } from '@/api'
import { BreadcrumbItem, Breadcrumbs } from '@/common/Breadcrumbs'
import { useKeplrWallet } from '@/contexts/KeplrWalletProvider'
import { formatAddress } from '@/helpers/formatters'
import { queryKeys } from '@/queries'
import { RoutePaths } from '@/routes'
import UiContainer from '@/ui/UiContainer'
import { UiSkeleton } from '@/ui/UiSkeleton'

import {
  getActionLabel,
  MODULE_REGISTRY,
  moduleLabelsMap,
  type SupportedModule,
} from './moduleRegistry'

export default function CreateProposalLayout() {
  const { account } = useParams<{ account: string }>()
  const location = useLocation()
  const [searchParams] = useSearchParams()

  const { address } = useKeplrWallet()

  const groupQuery = useQuery({
    queryKey: queryKeys.multisig.groupByAccount(account),
    enabled: Boolean(account),
    queryFn: () => fetchMultisigGroupByAccount(account ?? ''),
    staleTime: 30_000,
  })

  const isGroupMember = Boolean(
    address &&
    groupQuery.data?.members?.some(
      m => m.toLowerCase() === address.toLowerCase(),
    ),
  )

  if (groupQuery.isLoading) {
    return (
      <UiContainer>
        <div className='mt-26 space-y-6 px-4 md:px-0'>
          <UiSkeleton className='h-8 w-48' />
          <UiSkeleton className='h-4 w-64' />
        </div>
      </UiContainer>
    )
  }

  if (!isGroupMember) {
    return (
      <Navigate
        to={
          account
            ? generatePath(RoutePaths.MultisigGroup, { account })
            : RoutePaths.Multisig
        }
        replace
      />
    )
  }

  const paths = {
    group: account
      ? generatePath(RoutePaths.MultisigGroup, { account })
      : RoutePaths.Multisig,
    manual: account
      ? generatePath(RoutePaths.MultisigCreateProposalManual, { account })
      : RoutePaths.Multisig,
    constructor: account
      ? generatePath(RoutePaths.MultisigCreateProposalConstructor, {
          account,
        })
      : RoutePaths.Multisig,
  }

  const isManualMode = location.pathname.includes('/manual')
  const isConstructorMode = location.pathname.includes('/constructor')

  const selectedModule = searchParams.get('module')
  const selectedAction = searchParams.get('action')

  const buildBreadcrumbs = (): BreadcrumbItem[] => {
    const base: BreadcrumbItem[] = [
      { label: 'Home', to: RoutePaths.Root },
      { label: 'Groups', to: RoutePaths.Multisig },
      {
        label: formatAddress(account ?? '', 10, 10),
        to: paths.group,
      },
    ]

    if (isManualMode) {
      return [
        ...base,
        { label: 'Create proposal', to: paths.manual },
        { label: 'Manual' },
      ]
    }

    if (!isConstructorMode) {
      return [...base, { label: 'Create proposal' }]
    }

    const crumbs: BreadcrumbItem[] = [
      ...base,
      { label: 'Create proposal', to: paths.constructor },
      { label: 'Constructor', to: paths.constructor },
    ]

    if (selectedModule && selectedModule in MODULE_REGISTRY) {
      const module = selectedModule as SupportedModule

      crumbs.push({
        label: moduleLabelsMap[module],
        to: `${paths.constructor}?module=${module}`,
      })

      if (selectedAction) {
        const actionLabel = getActionLabel(module, selectedAction)

        if (actionLabel) {
          crumbs.push({ label: actionLabel })
        }
      }
    }

    return crumbs
  }

  if (!account) {
    return (
      <UiContainer>
        <div className='mt-26 space-y-6 px-4 md:px-0'>
          <h1 className='text-foreground text-xl font-semibold md:text-2xl'>
            Create Proposal
          </h1>
          <p className='text-muted-foreground text-sm'>
            Submit messages for multisig group
          </p>
          <p className='text-muted-foreground text-sm'>
            Group account is missing.
          </p>
        </div>
      </UiContainer>
    )
  }

  return (
    <UiContainer>
      <div className='mt-26 space-y-6 px-4 md:px-0'>
        <Breadcrumbs items={buildBreadcrumbs()} />

        <div className='space-y-1'>
          <h1 className='text-foreground text-xl font-semibold md:text-2xl'>
            Create Proposal
          </h1>
          <p className='text-muted-foreground text-sm wrap-break-word'>
            Submit messages for multisig group{' '}
            <span className='font-mono break-all'>{account}</span>
          </p>
        </div>

        <div className='border-border flex gap-1 border-b'>
          <NavLink
            to={paths.constructor}
            className={({ isActive }) =>
              `relative flex items-center gap-2 px-4 py-4 text-sm font-medium transition-colors ${
                isActive
                  ? 'text-foreground after:bg-primary after:absolute after:right-0 after:bottom-0 after:left-0 after:h-0.5'
                  : 'text-muted-foreground hover:text-foreground'
              }`
            }
          >
            Constructor
          </NavLink>

          <NavLink
            to={paths.manual}
            className={({ isActive }) =>
              `relative px-4 py-4 text-sm font-medium transition-colors ${
                isActive
                  ? 'text-foreground after:bg-primary after:absolute after:right-0 after:bottom-0 after:left-0 after:h-0.5'
                  : 'text-muted-foreground hover:text-foreground'
              }`
            }
          >
            Manual
          </NavLink>
        </div>

        <Outlet />
      </div>
    </UiContainer>
  )
}
